import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import ReactContentEditable from 'react-contenteditable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo } from '@fortawesome/pro-solid-svg-icons';

import AiInteraction from 'bundles/email/ai/interaction';
import { isEnabledPrompt } from 'bundles/email/ai/helpers';

class ContentEditable extends React.Component {
  static propTypes = {
    aiPromptKey: PropTypes.string,
    className: PropTypes.string,
    contentEditableStyle: PropTypes.object,
    contentEditableTagName: PropTypes.string.isRequired,
    defaultHtml: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    highlight: PropTypes.bool,
    override: PropTypes.string,
    renderHeader: PropTypes.func,
    sanitizer: PropTypes.func,
    wrapperRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    wrapperStyle: PropTypes.object,
    wrapperTagName: PropTypes.string.isRequired,

    onAiInteraction: PropTypes.func,
    onBlur: PropTypes.func,
    onClick: PropTypes.func,
    onDoubleClick: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDown: PropTypes.func,
    onOverrideChange: PropTypes.func.isRequired,
    onOverrideReset: PropTypes.func.isRequired
  }

  static defaultProps = {
    contentEditableTagName: 'div',
    wrapperTagName: 'div',
    onBlur: () => {},
    onFocus: () => {}
  }

  state = {
    // Hang onto and only use the inital value of defaultHtml - an external rerendering of this
    // component (e.g. via a reset all) in component_rendering.js will get any potentially modified
    // DOM content and update the prop with a value we don't want
    defaultHtml: this.props.defaultHtml,
    focused: false,
    hovering: false
  }

  handleChange = (event) => {
    let html = event.target.value;
    if (this.props.sanitizer) {
      html = this.props.sanitizer(html);
    }

    if (html === this.state.defaultHtml) {
      this.props.onOverrideReset();
    } else {
      this.props.onOverrideChange(html);
    }
  }

  handleBlur = () => {
    this.setState({ focused: false });
    this.props.onBlur();
  }

  handleFocus = () => {
    if (!this.props.disabled) {
      this.setState({ focused: true });
      this.props.onFocus();
    }
  }

  handleMouseEnter = () => {
    if (!this.props.disabled) {
      this.setState({ hovering: true });
    }
  }

  handleMouseLeave = () => {
    this.setState({ hovering: false });
  }

  handleReset = () => {
    // Clicking the reset button will remove the header on which the user just
    // clicked, which does not trigger mouseLeave
    this.setState({ hovering: false });
    this.props.onOverrideReset();
  }

  shouldRenderHeader(active, hasOverride) {
    return active && hasOverride;
  }

  render() {
    // const { className, highlight, override
    const active = this.state.focused || this.state.hovering;
    const hasOverride = this.props.override !== undefined;
    const html = hasOverride ? this.props.override : this.state.defaultHtml;

    const className = classNames('editable-component', this.props.className, undefined, {
      empty: !html,
      highlight: active || this.props.highlight
    });

    return React.createElement(this.props.wrapperTagName, {
      className,
      ref: this.props.wrapperRef,
      style: this.props.wrapperStyle,
      onClick: this.props.onClick,
      onDoubleClick: this.props.onDoubleClick,
      onMouseEnter: this.handleMouseEnter,
      onMouseLeave: this.handleMouseLeave
    }, this.renderChildren(html, active, hasOverride));
  }

  renderChildren(html, active, hasOverride) {
    const children = [];

    if (this.props.renderHeader) {
      const header = this.props.renderHeader(active, hasOverride);
      if (header) {
        children.push(header);
      }
    } else if (active && (hasOverride || isEnabledPrompt(this.props.aiPromptKey))) {
      children.push(this.renderHeader(hasOverride));
    }

    children.push(this.renderContentEditable(html));

    return children;
  }

  renderHeader(hasOverride) {
    return (
      <div className="control header" key="header" name="header">
        {!hasOverride ? <span/> :
          <span className="click-target" name="reset" onClick={this.handleReset}>
            <FontAwesomeIcon icon={faUndo} />
            Reset
          </span>
        }

        <AiInteraction
          promptKey={this.props.aiPromptKey}
          onClick={this.props.onAiInteraction}
        />
      </div>
    );
  }

  renderContentEditable(html) {
    return (
      <ReactContentEditable
        disabled={this.props.disabled}
        html={html}
        key="content"
        style={this.props.contentEditableStyle}
        tagName={this.props.contentEditableTagName}
        onBlur={this.handleBlur}
        onChange={this.handleChange}
        onFocus={this.handleFocus}
        onKeyDown={this.props.onKeyDown}
      />
    );
  }
}

export default ContentEditable;
